import React, { useState, useEffect, useRef } from 'react';
import { NT, NI, NMButton, NMChip } from './NaomiTheme';
import { MDialog, MTextField } from './NaomiUi';
import {
  getNaomiRegistry,
  registerNaomiPlugins,
  registerLivePreviewExtra,
  registerCanvasPreviewExtra,
} from './NaomiRegistry';

(function extendDataTypes() {
  const opts = window.DataTypeOptions2 || window.DataTypeOptions || [];
  if (!opts.find(o => o.value === 'image')) {
    opts.push({ value: 'image', label: 'Photo / image', icon: <NI.Eye/>, group: 'File' });
  }
  if (!opts.find(o => o.value === 'composite')) {
    opts.push({ value: 'composite', label: 'Custom (multi-field)', icon: <NI.Group/>, group: 'Composite' });
  }
  window.DataTypeOptions = opts;
  window.TypeMeta = window.TypeMeta || {};
  window.TypeMeta.image     = { icon: <NI.Eye/>,   label: 'Photo / image' };
  window.TypeMeta.composite = { icon: <NI.Group/>, label: 'Custom (multi-field)' };
})();

registerLivePreviewExtra('image', (item, value, set) => {
  const c = item.ImageConstraints || {};
  const hint = [c.accept || 'JPG, PNG, HEIC', c.maxSizeMB ? `≤ ${c.maxSizeMB} MB` : null].filter(Boolean).join(' · ');
  const ImageDropPickerComp = getNaomiRegistry().ImageDropPicker;
  return ImageDropPickerComp
    ? <ImageDropPickerComp value={value || null} onChange={set} height={120}
        label={`Tap to add photo · ${hint}`}/>
    : <div style={{ height: 100, border: `2px dashed ${NT.line2}`, borderRadius: 8 }}/>;
});

registerLivePreviewExtra('composite', (item, value, set) => {
  const def = (getNaomiRegistry().customTypes)[item.CompositeRef] || { fields: [] };
  const v = value || {};
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(def.fields.length, 4)}, 1fr)`, gap: 6 }}>
      {def.fields.map(f => (
        <div key={f.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 10.5, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4, fontWeight: 600 }}>
            {f.label}{f.unit ? ` (${f.unit})` : ''}
          </div>
          <input className="nm-input" type={f.type === 'number' ? 'number' : 'text'} value={(v as any)[f.id] ?? ''}
            onChange={e => set({ ...(v as any), [f.id]: e.target.value })}/>
        </div>
      ))}
    </div>
  );
});

registerCanvasPreviewExtra('image', (item) => {
  const c = item.ImageConstraints || {};
  const hint = [c.accept || 'JPG/PNG', c.maxSizeMB ? `≤${c.maxSizeMB}MB` : null].filter(Boolean).join(' · ');
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
      border: `1.5px dashed ${NT.line2}`, borderRadius: 6, background: NT.surfaceAlt }}>
      <span style={{ color: NT.ink3 }}><NI.Camera/></span>
      <div style={{ fontSize: 12, color: NT.ink3 }}>Photo upload <span style={{ color: NT.ink4 }}>· {hint}</span></div>
    </div>
  );
});

registerCanvasPreviewExtra('composite', (item) => {
  const def = getNaomiRegistry().customTypes[item.CompositeRef];
  if (!def) return <div style={{ fontSize: 12, color: NT.ink4, fontStyle: 'italic' }}>Custom type not defined</div>;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {def.fields.map(f => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
          background: NT.surface, border: `1px solid ${NT.line2}`, borderRadius: 4, fontSize: 12 }}>
          <span style={{ color: NT.ink3 }}>{f.label}</span>
          {f.unit && <span style={{ color: NT.ink4, fontSize: 11 }}>({f.unit})</span>}
        </div>
      ))}
    </div>
  );
});

function ImageConstraintsEditor({ value, onChange }) {
  const c = value || {};
  const update = (patch) => onChange({ ...c, ...patch });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10,
      background: NT.surfaceAlt, borderRadius: 6, border: `1px solid ${NT.line}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink2, textTransform: 'uppercase', letterSpacing: .4 }}>
        Image constraints
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: NT.ink3 }}>Accepted types</span>
          <select className="nm-input" value={c.accept || 'image/*'} onChange={e => update({ accept: e.target.value })}>
            <option value="image/*">Any image</option>
            <option value="image/jpeg,image/png">JPG, PNG only</option>
            <option value="image/jpeg,image/png,image/heic">JPG, PNG, HEIC</option>
            <option value="image/jpeg">JPG only</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: NT.ink3 }}>Max file size (MB)</span>
          <input className="nm-input" type="number" value={c.maxSizeMB ?? ''}
            onChange={e => { const n = parseInt(e.target.value); update({ maxSizeMB: isNaN(n) ? undefined : n }); }} placeholder="10"/>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: NT.ink3 }}>Min width (px)</span>
          <input className="nm-input" type="number" value={c.minWidth ?? ''}
            onChange={e => { const n = parseInt(e.target.value); update({ minWidth: isNaN(n) ? undefined : n }); }} placeholder="800"/>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <span style={{ fontSize: 11, color: NT.ink3 }}>Min height (px)</span>
          <input className="nm-input" type="number" value={c.minHeight ?? ''}
            onChange={e => { const n = parseInt(e.target.value); update({ minHeight: isNaN(n) ? undefined : n }); }} placeholder="600"/>
        </label>
      </div>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: NT.ink2 }}>
        <input type="checkbox" checked={!!c.requireExif} onChange={e => update({ requireExif: e.target.checked })}/>
        Require EXIF location data (geotagged)
      </label>
    </div>
  );
}
registerNaomiPlugins({ ImageConstraintsEditor });

function CustomTypeBuilder({ open, onClose, onSave, sdm, editing }) {
  const [name, setName] = React.useState(editing?.name || '');
  const [fields, setFields] = React.useState(editing?.fields || [
    { id: 'f1', label: 'Width',  type: 'number', unit: 'ft' },
    { id: 'f2', label: 'Height', type: 'number', unit: 'ft' },
  ]);
  React.useEffect(() => {
    if (open) {
      setName(editing?.name || '');
      setFields(editing?.fields || [
        { id: 'f1', label: 'Width',  type: 'number', unit: 'ft' },
        { id: 'f2', label: 'Height', type: 'number', unit: 'ft' },
      ]);
    }
  }, [open, editing]);

  const addField = () => setFields(fs => [...fs, { id: `f${fs.length+1}`, label: 'New field', type: 'text', unit: '' }]);
  const updateField = (i, patch) => setFields(fs => fs.map((f,idx) => idx===i ? { ...f, ...patch } : f));
  const removeField = (i) => setFields(fs => fs.filter((_,idx) => idx!==i));
  const move = (i, dir) => setFields(fs => {
    const a = fs.slice(); const j = i + dir;
    if (j < 0 || j >= a.length) return fs;
    [a[i], a[j]] = [a[j], a[i]]; return a;
  });

  const units = sdm?.BasicDefinition?.MeasurementUnit || [];

  return (
    <MDialog id="naomi-section-dialog-custom-type" open={open} onClose={onClose} title={editing ? 'Edit custom data type' : 'New custom data type'}
      icon={<NI.Group/>} width={640}
      actions={<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
        <NMButton id="naomi-section-button-custom-type-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
        <NMButton id="naomi-section-button-custom-type-save" color="primary" disabled={!name.trim() || fields.length === 0}
          onClick={() => { onSave({ name: name.trim(), fields }); onClose(); }}>
          {editing ? 'Save' : 'Create custom type'}
        </NMButton>
      </div>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: NT.ink2 }}>Type name</span>
          <input className="nm-input" autoFocus value={name} onChange={e => setName(e.target.value)}
            placeholder="e.g. Window dimensions, Material spec"/>
        </label>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: NT.ink2 }}>Fields ({fields.length})</span>
            <NMButton id="naomi-section-button-custom-type-add-field" size="sm" variant="outlined" color="neutral" icon={<NI.Plus/>} onClick={addField}>Add field</NMButton>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {fields.map((f, i) => (
              <div key={f.id} style={{ display: 'grid', gridTemplateColumns: '20px 1.4fr .8fr 1fr 26px 26px',
                gap: 6, alignItems: 'center', padding: 8, background: NT.surface,
                border: `1px solid ${NT.line2}`, borderRadius: 6 }}>
                <span style={{ fontSize: 11, color: NT.ink4, fontWeight: 600, textAlign: 'center' }}>{i+1}</span>
                <input className="nm-input" value={f.label} onChange={e => updateField(i, { label: e.target.value })} placeholder="Label"/>
                <select className="nm-input" value={f.type} onChange={e => updateField(i, { type: e.target.value })}>
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="boolean">Boolean</option>
                </select>
                <select className="nm-input" value={f.unit || ''} onChange={e => updateField(i, { unit: e.target.value })}
                  disabled={f.type !== 'number'}>
                  <option value="">— no unit —</option>
                  {units.map(u => <option key={u.OID} value={u.Symbol}>{u.Symbol} ({u.Name})</option>)}
                </select>
                <button id={`naomi-section-button-custom-type-field-move-up-${f.id}`} onClick={() => move(i, -1)} disabled={i===0} title="Move up"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: NT.ink3, opacity: i === 0 ? 0.3 : 1 }}>
                  <NI.Chev d="up"/>
                </button>
                <button id={`naomi-section-button-custom-type-field-remove-${f.id}`} onClick={() => removeField(i)} title="Remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: NT.danger }}>
                  <NI.Trash/>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: 10, background: NT.infoSoft, borderRadius: 6, border: `1px solid ${NT.info}22` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: NT.info, marginBottom: 4, textTransform: 'uppercase', letterSpacing: .4 }}>
            Preview
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(fields.length, 4)}, 1fr)`, gap: 6 }}>
            {fields.map(f => (
              <div key={f.id}>
                <div style={{ fontSize: 10.5, color: NT.ink3, textTransform: 'uppercase', letterSpacing: .4, fontWeight: 600 }}>
                  {f.label}{f.unit ? ` (${f.unit})` : ''}
                </div>
                <div style={{ height: 32, background: NT.surface, border: `1px solid ${NT.line2}`,
                  borderRadius: 4, marginTop: 2 }}/>
              </div>
            ))}
          </div>
        </div>
      </div>
    </MDialog>
  );
}
registerNaomiPlugins({ CustomTypeBuilder });

const isNumericSource = (i: any) =>
  i.DataType === 'integer' || i.DataType === 'currency' || i.DataType === 'float';

function CountSourcePicker({ value, sources, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = sources.find(s => s.OID === value);
  const selectedText = selected ? selected.Question.TranslatedText.text : '';

  const filtered = search.trim()
    ? sources.filter(s => {
        const q = (s.Question?.TranslatedText?.text || '').toLowerCase();
        return q.includes(search.toLowerCase()) || s.OID.toLowerCase().includes(search.toLowerCase());
      })
    : sources;

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false); setSearch('');
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button id="naomi-section-button-count-source-picker"
        onClick={() => { setOpen(o => !o); setSearch(''); }}
        style={{
          width: '100%', minHeight: 34, padding: '0 8px', borderRadius: 4,
          border: `1px solid ${open ? NT.primary : NT.line2}`,
          background: NT.surface, color: value ? NT.ink : NT.ink4,
          fontSize: 13, fontFamily: NT.font, textAlign: 'left', cursor: 'pointer', outline: 'none',
          display: 'flex', alignItems: 'center', gap: 6, boxSizing: 'border-box',
          transition: 'border-color .12s',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value
            ? <><span style={{ fontFamily: NT.fontMono, fontSize: 11, color: NT.ink3, marginRight: 5 }}>{value}</span>{selectedText}</>
            : '— pick a question —'}
        </span>
        <span style={{ color: NT.ink3, display: 'inline-flex', flexShrink: 0,
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <NI.Chev/>
        </span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 3px)', left: 0, right: 0, zIndex: 9999,
          background: NT.surface, border: `1.5px solid ${NT.primary}`, borderRadius: 8,
          boxShadow: NT.shadow3, overflow: 'hidden',
        }}>
          <div style={{ padding: '7px 8px', borderBottom: `1px solid ${NT.line}`,
            display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: NT.ink3, display: 'inline-flex', flexShrink: 0 }}><NI.Search/></span>
            <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search questions…"
              style={{ flex: 1, height: 28, border: 'none', background: 'transparent',
                color: NT.ink, fontSize: 12.5, fontFamily: NT.font, outline: 'none' }}/>
            {search && (
              <button id="naomi-section-button-count-search-clear" onClick={() => setSearch('')}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: NT.ink3, display: 'inline-flex', padding: 2 }}>
                <NI.Close/>
              </button>
            )}
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }} className="naomi-scroll">
            {filtered.length === 0 ? (
              <div style={{ padding: '14px 10px', color: NT.ink4, fontSize: 12.5, textAlign: 'center' }}>
                No questions match "{search}"
              </div>
            ) : filtered.map(s => {
              const qText = s.Question?.TranslatedText?.text || s.OID;
              const isSel = s.OID === value;
              return (
                <div key={s.OID}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(false); setSearch(''); onChange(s.OID); }}
                  style={{
                    padding: '7px 10px', cursor: 'pointer', fontSize: 13, fontFamily: NT.font,
                    display: 'flex', alignItems: 'baseline', gap: 8,
                    background: isSel ? NT.primarySoft : 'transparent',
                    color: isSel ? NT.primary2 : NT.ink,
                    borderLeft: isSel ? `3px solid ${NT.primary}` : '3px solid transparent',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = NT.surfaceAlt; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontFamily: NT.fontMono, fontSize: 11, color: NT.ink3, flexShrink: 0 }}>{s.OID}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{qText}</span>
                </div>
              );
            })}
          </div>
          {search && filtered.length > 0 && (
            <div style={{ padding: '5px 10px', borderTop: `1px solid ${NT.line}`,
              fontSize: 11, color: NT.ink4, fontFamily: NT.font }}>
              {filtered.length} of {sources.length} questions
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RepeatingGroupConfig({ group, sdm, onChange, summarizeInstances }) {
  const tv = sdm.TemplateVersion[0];
  const sources = tv.ItemDef.filter(isNumericSource);
  const repeating = group.Repeating === 'Yes';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12,
      background: NT.surfaceAlt, borderRadius: 6, border: `1px solid ${NT.line}` }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: NT.ink, fontWeight: 600 }}>
        <input type="checkbox" checked={repeating}
          onChange={e => onChange({ ...group, Repeating: e.target.checked ? 'Yes' : 'No' })}/>
        This section repeats (e.g. one entry per skylight, per slope, per damage area)
      </label>
      {repeating && (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 11, color: NT.ink3, fontWeight: 600 }}>Count comes from</span>
            {sources.length === 0 ? (
              <div style={{ fontSize: 11.5, color: NT.warn, padding: '6px 8px',
                background: NT.warnSoft, borderRadius: 4, border: `1px solid ${NT.warn}40` }}>
                No numeric questions yet. Add a number/integer question first and it will appear here.
              </div>
            ) : (
              <>
                <CountSourcePicker
                  value={group.RepeatFromOID || ''}
                  sources={sources}
                  onChange={oid => onChange({ ...group, RepeatFromOID: oid, __clearMethodOID: true })}
                />
                {!group.RepeatFromOID && (
                  <div style={{ fontSize: 11.5, color: NT.warn, padding: '5px 8px',
                    background: NT.warnSoft, borderRadius: 4, border: `1px solid ${NT.warn}40` }}>
                    Select a count source before saving.
                  </div>
                )}
              </>
            )}
          </label>

          <div style={{ borderTop: `1px solid ${NT.line}`, paddingTop: 10 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13,
              color: NT.ink, fontWeight: 600, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!summarizeInstances}
                onChange={e => onChange({ ...group, __summarizeInstances: e.target.checked })}/>
              Summarize instances
            </label>
          </div>
        </>
      )}
    </div>
  );
}
registerNaomiPlugins({ RepeatingGroupConfig });

window.bumpMinor = window.bumpMinor || ((v) => {
  if (!v) return '0.1';
  const parts = String(v).split('.').map(n => parseInt(n) || 0);
  parts[parts.length - 1] = (parts[parts.length - 1] || 0) + 1;
  return parts.join('.');
});

window.cleanLibrary = (lib) => (lib || []).filter(t => t && (t.items || 0) > 0);

function RichVersionsPane({ sdm, onPublish, onOpenVersion, onDiscardDraft }) {
  const history = sdm.history || [];
  const draft = sdm.draft;
  const tv = sdm.TemplateVersion[0];

  const draftChanges = draft?.changedSince || [];
  const byType = draftChanges.reduce((acc, c) => {
    const t = c.change || c.type || 'edit'; acc[t] = (acc[t] || 0) + 1; return acc;
  }, {});
  const deltaChips = [
    { key: 'add',    label: 'added',   color: NT.success },
    { key: 'edit',   label: 'edited',  color: NT.info },
    { key: 'remove', label: 'removed', color: NT.danger },
  ].filter(c => byType[c.key]);

  return (
    <div style={{ flex: 1, padding: 32, overflow: 'auto', background: NT.bg }}>
      <div style={{ maxWidth: 920, margin: '0 auto' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: NT.ink, marginBottom: 4 }}>Version history</div>
        <div style={{ fontSize: 13, color: NT.ink3, marginBottom: 24 }}>
          Click any version to open it in the builder. Drafts can be edited or discarded; published versions are read-only but can be forked.
        </div>

        {draft && (
          <div style={{ background: NT.surface, border: `2px solid ${NT.warn}`, borderRadius: 10,
            padding: 18, marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ padding: '3px 10px', background: NT.warnSoft, color: NT.warn,
                  borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5 }}>
                  Working draft
                </span>
                <span style={{ fontSize: 18, fontWeight: 700, color: NT.ink }}>v{draft.version}</span>
              </div>
              <div style={{ fontSize: 12, color: NT.ink3 }}>{tv.ItemDef.length} questions · {tv.ItemGroupDef.length} sections</div>
            </div>
            {draftChanges.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {deltaChips.map(c => (
                  <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 8px', background: c.color + '15', color: c.color,
                    borderRadius: 4, fontSize: 11.5, fontWeight: 600 }}>
                    <span style={{ fontWeight: 800 }}>{byType[c.key]}</span> {c.label}
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <NMButton id={`naomi-section-button-draft-publish-${draft.version}`} size="sm" color="primary" icon={<NI.Rocket/>} onClick={onPublish}>Publish v{draft.version}</NMButton>
              <NMButton id={`naomi-section-button-draft-open-${draft.version}`} size="sm" variant="outlined" color="neutral" icon={<NI.Edit/>}
                onClick={() => onOpenVersion({ version: draft.version, status: 'draft', author: 'You', date: 'now', note: 'Working draft', changes: draftChanges.length })}>
                Open in builder
              </NMButton>
              {draftChanges.length > 0 && (
                <NMButton id={`naomi-section-button-draft-discard-${draft.version}`} size="sm" variant="text" color="danger" onClick={onDiscardDraft}>Discard changes</NMButton>
              )}
            </div>
          </div>
        )}

        <div style={{ position: 'relative', paddingLeft: 26, borderLeft: `2px solid ${NT.line}`, marginLeft: 6 }}>
          {history.length === 0 ? (
            <div style={{ fontSize: 13, color: NT.ink4, padding: 16, fontStyle: 'italic' }}>
              No published versions yet. Publish your draft to start the version history.
            </div>
          ) : history.map((h, i) => (
            <RichHistoryRow key={i} h={h} sdm={sdm}/>
          ))}
        </div>
      </div>
    </div>
  );
}

function RichHistoryRow({ h, sdm }) {
  const isPublished = h.status === 'published';
  const isCurrent = isPublished && h === sdm.history.find(x => x.status === 'published');
  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      <div style={{ position: 'absolute', left: -34, top: 14, width: 14, height: 14, borderRadius: 7,
        background: isPublished ? NT.success : NT.line2, border: `3px solid ${NT.bg}` }}/>
      <div style={{ background: NT.surface, border: `1px solid ${NT.line}`,
          borderRadius: 8, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: NT.ink }}>v{h.version}</span>
            {isCurrent && (
              <span style={{ padding: '2px 8px', background: NT.successSoft, color: NT.success,
                borderRadius: 10, fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .4 }}>Live</span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: NT.ink3 }}>{h.date} · by {h.author}</div>
        </div>
        <div style={{ fontSize: 13, color: NT.ink2, marginBottom: 8 }}>{h.note}</div>
      </div>
    </div>
  );
}
registerNaomiPlugins({ RichVersionsPane });
